/**
 * Generic background-job envelope (`GET /v2/jobs/{job_id}`).
 *
 * The backend moved long-running work behind a single job resource: the
 * caller POSTs, gets a `job_id` back on a 202, and polls here until the
 * job reaches a terminal state. `result` is whatever that job type
 * returns — for `pricing_model_import` it is `{submission_id}`.
 *
 * Distinct from `GET /v2/submissions/upload/{job_id}` in
 * `submission-upload.ts`, which is the document-upload job and carries a
 * per-file breakdown this envelope has no concept of. The two share the
 * same `JobStatus` vocabulary and nothing else.
 */
import { ApiError, getJsonReal } from "../client";

/** Same five values the upload job uses. */
export type JobStatus = "queued" | "running" | "completed" | "partial" | "failed";

export interface JobGet {
    id: string;
    /** e.g. `"pricing_model_import"`. */
    type: string;
    status: JobStatus;
    /** Job-type specific payload, present once the job succeeds. */
    result: Record<string, unknown> | null;
    /** Failure detail. Carries the same formatted string the synchronous
     *  version used to return as a 422 body, so `pricingModelIssues` can
     *  still parse per-cell problems out of it. */
    error: string | null;
    attempts: number;
    created_at: string;
    started_at: string | null;
    finished_at: string | null;
}

const TERMINAL: ReadonlySet<JobStatus> = new Set(["completed", "partial", "failed"]);

/** Reading the job again cannot fix these — a missing job or a dead
 *  session stays that way, so retrying to the deadline would just hold
 *  the user on a spinner for minutes. */
const PERMANENT_STATUSES: ReadonlySet<number> = new Set([401, 403, 404]);

export function getJob(jobId: string): Promise<JobGet> {
    return getJsonReal<JobGet>(`/v2/jobs/${encodeURIComponent(jobId)}`);
}

/**
 * Poll a job until it settles, or the timeout elapses.
 *
 * Resolves with the final envelope, or `"timeout"` if it never settles —
 * the caller decides what that means. Transient GET failures are
 * swallowed and retried to the deadline: a network blip must not be
 * reported as an import failure when the worker is still going. A
 * PERMANENT failure (401/403/404) throws instead, immediately — see
 * `PERMANENT_STATUSES`.
 *
 * `onPoll` fires after every successful poll, including non-terminal
 * ones, so callers can drive a progress indicator.
 */
export async function waitForJob(
    jobId: string,
    opts: { intervalMs?: number; timeoutMs?: number; onPoll?: (job: JobGet) => void } = {},
): Promise<JobGet | "timeout"> {
    const intervalMs = opts.intervalMs ?? 2000;
    const timeoutMs = opts.timeoutMs ?? 180_000;
    const deadline = Date.now() + timeoutMs;
    for (;;) {
        try {
            const job = await getJob(jobId);
            opts.onPoll?.(job);
            if (TERMINAL.has(job.status)) return job;
        } catch (error) {
            if (error instanceof ApiError && PERMANENT_STATUSES.has(error.status)) throw error;
            // Transient — ignore and retry until the deadline.
        }
        const remaining = deadline - Date.now();
        if (remaining <= 0) return "timeout";
        // Clamped so the last sleep can't overshoot the promised timeout.
        await new Promise((resolve) => setTimeout(resolve, Math.min(intervalMs, remaining)));
    }
}
